import { Button, Text } from "@ttc3k/trekker";
import { Box, Flex, Grid } from "styled-system/jsx";
import { useScratchPad, UseScratchPadProps } from "./useScratchPad";
import { useTranslation } from "react-i18next";
import { AutogrowTextarea } from "./AutogrowTextarea";
import { css } from "styled-system/css";

export const ScratchPad = ({ person }: UseScratchPadProps) => {
  const { t } = useTranslation("people", {
    keyPrefix: "PERSON_DETAILS_MODAL.SCRATCHPAD",
  });

  const {
    isEditing,
    setIsEditing,
    scratchpad,
    setScratchpad,
    handleSave,
    isSaving,
  } = useScratchPad({ person });

  return (
    <Grid
      gridTemplateRows="48px 196px"
      gridGap="0"
      border="1px solid"
      borderColor="token(colors.ink.300)"
      bgColor="#F7FAFF" // NOTE: This color isn't in Trekker
    >
      <Flex
        justifyContent="space-between"
        alignItems="center"
        px="300"
        py="150"
        borderBottom="1px solid"
        borderColor="border.error"
      >
        <Text visual="bodySemiBold">{t("TITLE")}</Text>
        <Flex alignItems="center" gap="200">
          {isEditing ? (
            <>
              <Button
                visual="unstyled"
                type="button"
                onClick={() => {
                  setScratchpad(person?.scratchpad ?? "");
                  setIsEditing(false);
                }}
                color="text.mid"
                _hover={{
                  color: "text.dark",
                }}
                _focus={{
                  color: "text.dark",
                }}
              >
                {t("BUTTONS.CANCEL")}
              </Button>
              <Button
                visual="unstyled"
                type="button"
                onClick={handleSave}
                color="text.accent.blue.mid"
                _hover={{
                  color: "text.accent.blue.dark",
                }}
                _focus={{
                  color: "text.accent.blue.dark",
                }}
                // The loading state is weird with unstyled buttons, so we'll just disable it instead
                disabled={isSaving}
              >
                {t("BUTTONS.SAVE")}
              </Button>
            </>
          ) : (
            <Button
              visual="unstyled"
              type="button"
              onClick={() => setIsEditing(true)}
              color="text.accent.blue.mid"
              _hover={{
                color: "text.accent.blue.dark",
              }}
              _focus={{
                color: "text.accent.blue.dark",
              }}
            >
              {t("BUTTONS.EDIT")}
            </Button>
          )}
        </Flex>
      </Flex>
      <Box
        h="196px"
        overflowY="auto"
        bg="repeating-linear-gradient(to bottom, #E4EDFC, #E4EDFC 1px, transparent 1px, transparent 24.5px)"
        bgAttachment="local"
      >
        {isEditing ? (
          <AutogrowTextarea
            value={scratchpad}
            onChange={e => setScratchpad(e.target.value)}
            props={{
              name: "scrathpad",
              placeholder: t("PLACEHOLDER"),
              "aria-label": t("TITLE"),
              className: css({
                color: "text.dark",
                fontSize: "16px",
                fontFamily: "var(--wa-fonts-body)",
                lineHeight: "24.5px",
                whiteSpace: "pre-wrap",
                p: "300",
                pt: "25px",
                w: "100%",
                minH: "190px",
                outline: "none",
                border: "none",
                bg: "transparent",
              }),
            }}
          />
        ) : (
          <Text
            visual="bodyRegular"
            color={scratchpad ? "text.dark" : "text.light"}
            lineHeight="24.5px"
            whiteSpace="pre-wrap"
            p="300"
            pt="25px"
          >
            {scratchpad || t("PLACEHOLDER")}
          </Text>
        )}
      </Box>
    </Grid>
  );
};
